export enum ProcessType {
  SCRIPT = "SCRIPT",
  PIXEL = "PIXEL",
}

interface ScriptEntry {
  src: (() => void) | string;
  resolve: ((value?: unknown) => void) | undefined;
  promise: Promise<unknown> | undefined;
  processType: ProcessType;
}

export default class ScriptLoader {
  private scripts: ScriptEntry[] = [];
  private loading: boolean = false;
  private currentScriptIndex: number = 0;

  addScript(source: (() => void) | string, processType: ProcessType = ProcessType.SCRIPT): Promise<unknown> | undefined {
    if (this.doesScriptExist(source)) {
      return this.getScript(source)?.promise;
    }

    const scriptObj: ScriptEntry = {
      src: source,
      resolve: undefined,
      promise: undefined,
      processType,
    };

    scriptObj.promise = new Promise((resolve) => {
      scriptObj.resolve = resolve;
    });

    this.scripts.push(scriptObj);
    this.triggerQueueCheck();
    return scriptObj.promise;
  }

  private getScript(source: (() => void) | string): ScriptEntry | undefined {
    return this.scripts.find((s) => s.src === source);
  }

  private doesScriptExist(source: (() => void) | string): boolean {
    return this.scripts.find((s) => s.src === source) !== undefined;
  }

  private triggerQueueCheck(): void {
    if (this.loading) {
      return;
    }
    this.processScripts();
  }

  private scriptLoadedCallback(script: ScriptEntry): void {
    this.loading = false;
    this.currentScriptIndex++;
    script.resolve?.();
    if (this.currentScriptIndex < this.scripts.length) {
      this.triggerQueueCheck();
    }
  }

  private processScripts(): void {
    this.loading = true;
    const scriptToProcess = this.scripts[this.currentScriptIndex];
    switch (scriptToProcess.processType) {
      case ProcessType.PIXEL:
        this.processViaPixelEmission(scriptToProcess);
        break;
      case ProcessType.SCRIPT:
      default:
        this.processViaScript(scriptToProcess);
        break;
    }
  }

  private processViaScript(scriptToProcess: ScriptEntry): void {
    (scriptToProcess.src as () => void)();
    this.scriptLoadedCallback(scriptToProcess);
  }

  private processViaPixelEmission(scriptToProcess: ScriptEntry): void {
    const iFrame = document.createElement("iframe");
    iFrame.width = "1";
    iFrame.height = "1";
    iFrame.style.display = "none";
    document.body.appendChild(iFrame);
    iFrame.onload = () => this.scriptLoadedCallback(scriptToProcess);
    iFrame.contentWindow!.document.open();
    iFrame.contentWindow!.document.write(scriptToProcess.src as string);
    iFrame.contentWindow!.document.close();
  }
}
